import { lazy, useEffect, useState } from 'react'

import {
  ChevronLeft,
  ChevronRight,
  Heart,
  Menu,
  Search,
  ShoppingBag,
  User,
  X,
} from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import AnnouncementBar from './AnnouncementBar.jsx'
import AdminHQ from './AdminHQ.jsx'
import DeferredBoundary from './DeferredBoundary.jsx'
import { supabase } from '../supabaseClient.js'
import { getCollectionPathForVibe } from '../utils/collections.js'
import { startRazorpayCheckout } from '../utils/razorpayCheckout.js'
import { getPolicyViewFromLocation } from '../utils/policyRouting.js'

const MilieuModal = lazy(() => import('./MilieuModal.jsx'))

const activeVibePillClassByValue = {
  her_vibe:
    'shadow-[inset_4px_4px_8px_rgba(0,0,0,0.1),_inset_-4px_-4px_8px_rgba(255,255,255,1),_inset_0_0_0_1px_rgba(199,21,133,0.32),_inset_0_0_14px_rgba(199,21,133,0.12)]',
  boys_vibe:
    'shadow-[inset_4px_4px_8px_rgba(0,0,0,0.1),_inset_-4px_-4px_8px_rgba(255,255,255,1),_inset_0_0_0_1px_rgba(0,80,255,0.32),_inset_0_0_14px_rgba(0,80,255,0.12)]',
  mini_vibe:
    'shadow-[inset_4px_4px_8px_rgba(0,0,0,0.1),_inset_-4px_-4px_8px_rgba(255,255,255,1),_inset_0_0_0_1px_rgba(0,200,80,0.32),_inset_0_0_14px_rgba(0,200,80,0.12)]',
  hype_vibe:
    'shadow-[inset_4px_4px_8px_rgba(0,0,0,0.1),_inset_-4px_-4px_8px_rgba(255,255,255,1),_inset_0_0_0_1px_rgba(106,13,173,0.32),_inset_0_0_14px_rgba(106,13,173,0.12)]',
  univibe:
    'shadow-[inset_4px_4px_8px_rgba(0,0,0,0.1),_inset_-4px_-4px_8px_rgba(255,255,255,1),_inset_0_0_0_1px_rgba(17,24,39,0.22),_inset_0_0_14px_rgba(17,24,39,0.08)]',
}

const navItems = [
  {
    value: 'her_vibe',
    firstWord: 'Her',
    sparkleClass: 'text-[#C71585]',
    hoverGlowClass: 'hover:drop-shadow-[0_15px_12px_rgba(199,21,133,0.9)]',
  },
  {
    value: 'boys_vibe',
    firstWord: 'His',
    sparkleClass: 'text-[#0050FF]',
    hoverGlowClass: 'hover:drop-shadow-[0_15px_12px_rgba(0,80,255,0.9)]',
  },
  {
    value: 'mini_vibe',
    firstWord: 'Mini',
    sparkleClass: 'text-[#00C850]',
    hoverGlowClass: 'hover:drop-shadow-[0_15px_12px_rgba(0,200,80,0.9)]',
  },
  {
    value: 'hype_vibe',
    firstWord: 'Hype',
    sparkleClass: 'text-[#6A0DAD]',
    hoverGlowClass: 'hover:drop-shadow-[0_15px_12px_rgba(106,13,173,0.9)]',
  },
  {
    value: 'univibe',
    firstWord: 'Uni',
    sparkleClass: 'text-[#111827]',
    hoverGlowClass: 'hover:drop-shadow-[0_15px_12px_rgba(17,24,39,0.55)]',
  },
]

const categoryNavItems = [
  {
    label: 'Caps & Hats',
    to: '/collections/caps-hats',
  },
  {
    label: 'Combo Deals',
    to: '/collections/combo-deals',
  },
]

const megaSearchSuggestions = [
  'Oversized Tees',
  'Kaftans',
  'Minimalist Totes',
  'Winter Hoodies',
]

function SparkleIcon({ className = '' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={`pointer-events-none absolute -top-4 left-1/2 h-4 w-4 -translate-x-1/2 scale-75 ${className}`}
    >
      <path d="M12 2.75V7.5M12 16.5V21.25M2.75 12H7.5M16.5 12H21.25" />
    </svg>
  )
}

function CategoryLabel({ firstWord, sparkleClass }) {
  return (
    <span className="inline-flex items-center gap-1 leading-none font-bold text-black">
      <span>{firstWord}</span>
      <span className="inline-flex items-center">
        Vib
        <span className="relative">
          e
          <SparkleIcon className={sparkleClass} />
        </span>
      </span>
    </span>
  )
}

const authInputClassName =
  'w-full border-b border-gray-800 bg-transparent px-2 py-1 text-sm text-white outline-none transition-colors placeholder:text-white/30 focus:border-sky-400'

const otpInputClassName =
  'w-full border-b border-gray-800 bg-transparent px-2 py-1 text-sm tracking-[0.28em] text-white outline-none transition-colors placeholder:text-white/20 focus:border-sky-400'

const authLinkClassName =
  'text-[11px] font-medium text-gray-400 transition-colors hover:text-white'

const authPrimaryButtonClassName =
  'rounded-full bg-sky-400 px-4 py-2 text-xs font-semibold text-black transition-transform duration-200 hover:scale-105 hover:bg-sky-300 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/30'

const signOutButtonClassName =
  'rounded-full border border-gray-800 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white hover:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/15'

const CTO_EMAIL = 'advdeepaknarwal8@gmail.com'

function getMilieuViewFromCurrentLocation() {
  return getPolicyViewFromLocation(window.location.pathname, window.location.hash)
}

function normalizeOtpError(error, context) {
  const rawMessage =
    typeof error?.message === 'string'
      ? error.message
      : typeof error === 'string'
        ? error
        : ''
  const message = rawMessage.toLowerCase()

  if (context === 'send') {
    if (
      message.includes('rate') ||
      message.includes('limit') ||
      message.includes('frequency') ||
      message.includes('60')
    ) {
      return 'Wait 60s before resending.'
    }

    if (message.includes('email')) {
      return 'Enter a valid email.'
    }

    return 'Unable to send code right now.'
  }

  if (
    message.includes('invalid') ||
    message.includes('expired') ||
    message.includes('token') ||
    message.includes('otp')
  ) {
    return 'Invalid OTP.'
  }

  return 'Unable to verify code right now.'
}

function IconButton({ label, children, onClick, className = '', ...buttonProps }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={`grid h-8 w-8 place-items-center rounded-full text-black transition-transform duration-200 hover:scale-105 hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/15 sm:h-9 sm:w-9 ${className}`}
      {...buttonProps}
    >
      {children}
    </button>
  )
}

function VibeButton({
  activeVibe,
  item,
  setActiveVibe,
  value,
  onClick = () => {},
  to = '',
  className = '',
}) {
  const isActive = activeVibe === value
  const activePillShadow =
    activeVibePillClassByValue[value] ?? activeVibePillClassByValue.her_vibe
  const navigate = useNavigate()

  return (
    <button
      type="button"
      onClick={() => {
        setActiveVibe(value)
        onClick()
        if (to) {
          navigate(to)
        }
      }}
      aria-pressed={isActive}
      className={`group relative inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold text-black transition-all duration-300 ${
        isActive
          ? `bg-[#f8f9fa] border border-gray-100 ${activePillShadow}`
          : `bg-transparent ${item.hoverGlowClass} hover:-translate-y-1`
      } ${className}`}
    >
      <CategoryLabel firstWord={item.firstWord} sparkleClass={item.sparkleClass} />
      {!isActive ? (
        <span
          className={`absolute inset-x-0 -bottom-0.5 h-px bg-black transition-transform duration-200 ${
            isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
          }`}
        />
      ) : null}
    </button>
  )
}

function HeaderPageLink({
  to,
  children,
  isActive = false,
  onClick = () => {},
  className = '',
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      className={`inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full border px-4 py-2 text-sm font-bold transition-all duration-300 ${
        isActive
          ? 'border-black bg-black text-white shadow-[0_10px_20px_rgba(0,0,0,0.12)]'
          : 'border-black/10 bg-white text-black hover:-translate-y-1 hover:border-black hover:bg-black hover:text-white'
      } ${className}`}
    >
      {children}
    </Link>
  )
}

const rotaryDialSlotStyles = [
  {
    transform:
      'perspective(1600px) translateY(0px) translateZ(42px) rotateY(0deg) scale(1)',
    opacity: 1,
    zIndex: 40,
  },
  {
    transform:
      'perspective(1600px) translateY(1px) translateZ(18px) rotateY(-12deg) scale(0.965)',
    opacity: 0.94,
    zIndex: 30,
  },
  {
    transform:
      'perspective(1600px) translateY(2px) translateZ(-2px) rotateY(12deg) scale(0.91)',
    opacity: 0.8,
    zIndex: 20,
  },
  {
    transform:
      'perspective(1600px) translateY(4px) translateZ(-18px) rotateY(26deg) scale(0.84)',
    opacity: 0.68,
    zIndex: 10,
  },
]

function RotaryDialNav({
  activeVibe,
  isCapsHatsPage = false,
  isComboDealsPage = false,
  isAboutPage = false,
  isCustomVibePage = false,
  isOffersPage = false,
  isSalePage = false,
  onItemSelect = () => {},
  setActiveVibe = () => {},
}) {
  const [rotaryIndex, setRotaryIndex] = useState(0)

  const dialItems = [
    {
      key: 'her_vibe',
      type: 'vibe',
      value: 'her_vibe',
      item: navItems[0],
      to: getCollectionPathForVibe('her_vibe'),
    },
    {
      key: 'boys_vibe',
      type: 'vibe',
      value: 'boys_vibe',
      item: navItems[1],
      to: getCollectionPathForVibe('boys_vibe'),
    },
    {
      key: 'mini_vibe',
      type: 'vibe',
      value: 'mini_vibe',
      item: navItems[2],
      to: getCollectionPathForVibe('mini_vibe'),
    },
    {
      key: 'hype_vibe',
      type: 'vibe',
      value: 'hype_vibe',
      item: navItems[3],
      to: getCollectionPathForVibe('hype_vibe'),
    },
    {
      key: 'univibe',
      type: 'vibe',
      value: 'univibe',
      item: navItems[4],
      to: getCollectionPathForVibe('univibe'),
    },
    {
      key: 'about',
      type: 'link',
      to: '/about',
      label: 'ABOUT US',
      isActive: isAboutPage,
    },
    {
      key: 'custom-vibe',
      type: 'link',
      to: '/custom-vibe',
      label: 'Custom Vibe',
      isActive: isCustomVibePage,
    },
    {
      key: 'offers',
      type: 'link',
      to: '/offers',
      label: 'OFFERS',
      isActive: isOffersPage,
    },
    {
      key: 'sale',
      type: 'link',
      to: '/sale',
      label: 'SALE',
      isActive: isSalePage,
    },
    {
      key: 'caps-hats',
      type: 'link',
      to: '/collections/caps-hats',
      label: 'Caps & Hats',
      isActive: isCapsHatsPage,
    },
    {
      key: 'combo-deals',
      type: 'link',
      to: '/collections/combo-deals',
      label: 'Combo Deals',
      isActive: isComboDealsPage,
    },
  ]

  const visibleItems = Array.from({ length: 4 }, (_, slot) => {
    const index = (rotaryIndex + slot) % dialItems.length
    return {
      ...dialItems[index],
      slot,
    }
  })

  function rotateDial(direction) {
    setRotaryIndex((current) => {
      const next = current + direction

      if (next < 0) {
        return dialItems.length - 1
      }

      return next % dialItems.length
    })
  }

  return (
    <div className="hidden min-w-0 flex-1 items-center justify-center gap-2 lg:flex">
      <button
        type="button"
        onClick={() => rotateDial(-1)}
        aria-label="Rotate menu left"
        className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-black/10 bg-white text-black transition-all duration-300 hover:-translate-y-0.5 hover:border-black hover:bg-black hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/15"
      >
        <ChevronLeft className="h-4 w-4" strokeWidth={2} />
      </button>

      <div className="min-w-0 flex-1 overflow-hidden">
        <div className="grid w-full grid-cols-4 gap-2 [perspective:1600px]">
          {visibleItems.map((item) => {
            const slotStyle = rotaryDialSlotStyles[item.slot]

            return (
              <div
                key={item.key}
                className="relative min-w-0 transition-all duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)]"
                style={slotStyle}
              >
                {item.type === 'vibe' ? (
                  <VibeButton
                    activeVibe={activeVibe}
                    item={item.item}
                    setActiveVibe={setActiveVibe}
                    value={item.value}
                    to={item.to}
                    onClick={onItemSelect}
                    className="w-full px-3 py-2 text-[11px]"
                  />
                ) : (
                  <HeaderPageLink
                    to={item.to}
                    isActive={item.isActive}
                    onClick={onItemSelect}
                    className="w-full px-3 py-2 text-[11px]"
                  >
                    {item.label}
                  </HeaderPageLink>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <button
        type="button"
        onClick={() => rotateDial(1)}
        aria-label="Rotate menu right"
        className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-black/10 bg-white text-black transition-all duration-300 hover:-translate-y-0.5 hover:border-black hover:bg-black hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/15"
      >
        <ChevronRight className="h-4 w-4" strokeWidth={2} />
      </button>
    </div>
  )
}

function CartButton({ count, onClick }) {
  return (
    <button
      type="button"
      aria-label="Cart"
      onClick={onClick}
      className="relative grid h-8 w-8 place-items-center rounded-full text-black transition-transform duration-200 hover:scale-105 hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/15 sm:h-9 sm:w-9"
    >
      <ShoppingBag className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.75} />
      {count > 0 ? (
        <span className="absolute -right-0.5 -top-0.5 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-black px-1 text-[10px] font-semibold leading-none text-white">
          {count > 9 ? '9+' : count}
        </span>
      ) : null}
    </button>
  )
}

function AuthPanel({
  authMode = 'default',
  authMessage = '',
  email = '',
  isOtpSent = false,
  loading = false,
  onOpenAdminHQ = () => {},
  onOpenDropBag = () => {},
  onOpenLoginMode = () => {},
  onOpenMilieu = () => {},
  onOpenSignupMode = () => {},
  onSendOtp = () => {},
  onSignOut = () => {},
  onVerifyOtp = () => {},
  otp = '',
  setEmail = () => {},
  setAuthMessage = () => {},
  setOtp = () => {},
  user = null,
}) {
  if (user) {
    return (
      <>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-400">
            Welcome Back
          </p>
          <p className="mt-3 break-all text-sm font-medium text-white">
            Welcome, {user.email}
          </p>
        </div>

        <button
          type="button"
          onClick={onSignOut}
          disabled={loading}
          className={signOutButtonClassName}
        >
          Sign Out
        </button>

        {user?.email === CTO_EMAIL ? (
          <button
            type="button"
            onClick={onOpenAdminHQ}
            className="mt-4 w-full text-left text-sm font-bold text-red-400 hover:text-red-300"
          >
            CTO Dashboard 👑
          </button>
        ) : null}

        <hr className="my-4 border-gray-800" />

        <button
          type="button"
          onClick={onOpenMilieu}
          className="flex w-full items-center justify-between rounded-xl px-1 py-2 text-sm font-medium text-gray-300 transition-transform duration-200 hover:translate-x-1 hover:font-semibold hover:text-white"
        >
          <span>Milieu</span>
          <span className="text-xs text-gray-500">Settings</span>
        </button>

        <button
          type="button"
          onClick={onOpenDropBag}
          className="mt-2 w-full text-left text-sm text-gray-400 transition-colors hover:text-white"
        >
          dropping a bag
        </button>
      </>
    )
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-400">
            {authMode === 'login' ? 'Welcome Back' : 'Join the Vibe'}
          </p>
        </div>
      </div>

      <form
        className="mt-3 space-y-3"
        onSubmit={isOtpSent ? onVerifyOtp : onSendOtp}
      >
        {!isOtpSent ? (
          <input
            id="auth-email"
            type="email"
            name="email"
            placeholder="your@email.com"
            autoComplete="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
              setAuthMessage('')
            }}
            disabled={loading}
            className={authInputClassName}
          />
        ) : (
          <input
            id="auth-otp"
            type="text"
            name="otp"
            placeholder="6-digit code"
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={otp}
            onChange={(event) => {
              setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))
              setAuthMessage('')
            }}
            disabled={loading}
            className={otpInputClassName}
          />
        )}

        {authMessage ? (
          <p className="pt-1 text-[11px] leading-5 text-red-500">
            {authMessage}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={loading}
          className={authPrimaryButtonClassName}
        >
          {loading ? 'Working...' : isOtpSent ? 'Verify' : 'Send OTP'}
        </button>
      </form>

      <div className="mt-3 flex flex-col gap-2">
        {authMode === 'login' ? (
          <>
            <button
              type="button"
              onClick={onOpenLoginMode}
              className={authLinkClassName}
            >
              Login with OTP instead
            </button>
            <button
              type="button"
              onClick={onOpenSignupMode}
              className={authLinkClassName}
            >
              Need an account? Sign Up
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={onOpenLoginMode}
            className={authLinkClassName}
          >
            Already have a vibe? Login
          </button>
        )}
      </div>

      <hr className="my-4 border-gray-800" />

      <button
        type="button"
        onClick={onOpenMilieu}
        className="flex w-full items-center justify-between rounded-xl px-1 py-2 text-sm font-medium text-gray-300 transition-transform duration-200 hover:translate-x-1 hover:font-semibold hover:text-white"
      >
        <span>Milieu</span>
        <span className="text-xs text-gray-500">Settings</span>
      </button>

      <button
        type="button"
        onClick={onOpenDropBag}
        className="mt-2 w-full text-left text-sm text-gray-400 transition-colors hover:text-white"
      >
        dropping a bag
      </button>
    </>
  )
}

function Navbar({
  activeVibe = 'all',
  setActiveVibe = () => {},
  searchQuery = '',
  setSearchQuery = () => {},
  cartItems = [],
  setIsCartOpen = () => {},
  reviews = [],
  setReviews = () => {},
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const [user, setUser] = useState(null)
  const [authMode, setAuthMode] = useState('default')
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [isOtpSent, setIsOtpSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [authMessage, setAuthMessage] = useState('')
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const [isMilieuOpen, setIsMilieuOpen] = useState(false)
  const [milieuActiveView, setMilieuActiveView] = useState('menu')
  const [showAdminHQ, setShowAdminHQ] = useState(false)
  const [showDropBagModal, setShowDropBagModal] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isMobileVibesExpanded, setIsMobileVibesExpanded] = useState(false)
  const [isMegaSearchOpen, setIsMegaSearchOpen] = useState(false)
  const [bagAmount, setBagAmount] = useState(100)
  const [customBagAmount, setCustomBagAmount] = useState('')
  const cartCount = cartItems.length
  const isHomePage = location.pathname === '/'
  const isAboutPage = location.pathname === '/about'
  const isCustomVibePage = location.pathname === '/custom-vibe'
  const isOffersPage = location.pathname === '/offers'
  const isSalePage = location.pathname === '/sale'
  const isCapsHatsPage = location.pathname === '/collections/caps-hats'
  const isComboDealsPage = location.pathname === '/collections/combo-deals'
  const menuItems = [
    {
      label: 'ALL VIBES',
      type: 'dropdown',
      links: [
        {
          ...navItems.find((item) => item.value === 'her_vibe'),
          path: getCollectionPathForVibe('her_vibe'),
        },
        {
          ...navItems.find((item) => item.value === 'boys_vibe'),
          path: getCollectionPathForVibe('boys_vibe'),
        },
        {
          ...navItems.find((item) => item.value === 'hype_vibe'),
          path: getCollectionPathForVibe('hype_vibe'),
        },
        {
          ...navItems.find((item) => item.value === 'mini_vibe'),
          path: getCollectionPathForVibe('mini_vibe'),
        },
        {
          ...navItems.find((item) => item.value === 'univibe'),
          path: getCollectionPathForVibe('univibe'),
        },
      ].filter(Boolean),
    },
    {
      label: 'CAPS & HATS',
      type: 'link',
      path: '/collections/caps-hats',
      isActive: isCapsHatsPage,
    },
    {
      label: 'COMBO DEALS',
      type: 'link',
      path: '/collections/combo-deals',
      isActive: isComboDealsPage,
    },
    {
      label: 'CUSTOM VIBE',
      type: 'link',
      path: '/custom-vibe',
      isActive: isCustomVibePage,
    },
    {
      label: 'OFFERS',
      type: 'link',
      path: '/offers',
      isActive: isOffersPage,
    },
    {
      label: 'SALE',
      type: 'link',
      path: '/sale',
      isActive: isSalePage,
    },
    {
      label: 'ABOUT US',
      type: 'link',
      path: '/about',
      isActive: isAboutPage,
    },
  ]

  useEffect(() => {
    setIsMobileMenuOpen(false)
    setIsMobileVibesExpanded(false)
    setIsMegaSearchOpen(false)
  }, [activeVibe])

  useEffect(() => {
    if (!supabase?.auth) {
      return undefined
    }

    let isActive = true

    async function loadSession() {
      try {
        const { data } = await supabase.auth.getSession()

        if (isActive) {
          setUser(data.session?.user ?? null)
        }
      } catch {
        if (isActive) {
          setUser(null)
        }
      }
    }

    loadSession()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (isActive) {
        setUser(session?.user ?? null)
      }
    })

    return () => {
      isActive = false
      subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    function syncMilieuLocation() {
      const nextView = getMilieuViewFromCurrentLocation()

      if (nextView) {
        setMilieuActiveView(nextView)
        setIsMilieuOpen(true)
        return
      }

      setMilieuActiveView('menu')
      setIsMilieuOpen(false)
    }

    syncMilieuLocation()

    window.addEventListener('popstate', syncMilieuLocation)
    window.addEventListener('hashchange', syncMilieuLocation)

    return () => {
      window.removeEventListener('popstate', syncMilieuLocation)
      window.removeEventListener('hashchange', syncMilieuLocation)
    }
  }, [location.pathname, location.hash])

  function resetAuthFlow({ clearEmail = false } = {}) {
    setOtp('')
    setIsOtpSent(false)
    setAuthMessage('')

    if (clearEmail) {
      setEmail('')
    }
  }

  function openLoginMode() {
    setAuthMode('login')
    resetAuthFlow()
  }

  function openSignupMode() {
    setAuthMode('signup')
    resetAuthFlow()
  }

  function closeTransientPanels() {
    setIsMegaSearchOpen(false)
    setIsProfileOpen(false)
    setIsMobileMenuOpen(false)
    setIsMilieuOpen(false)
    setShowAdminHQ(false)
    setShowDropBagModal(false)
    setIsCartOpen(false)
  }

  async function handleSendOtp(event) {
    if (event?.preventDefault) {
      event.preventDefault()
    }

    setAuthMessage('')

    if (!supabase?.auth) {
      setAuthMessage('Supabase auth is not configured.')
      return
    }

    const trimmedEmail = email.trim()

    if (!trimmedEmail) {
      setAuthMessage('Enter a valid email.')
      return
    }

    setLoading(true)

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: trimmedEmail,
      })

      if (error) {
        throw error
      }

      setIsOtpSent(true)
      setOtp('')
      setAuthMessage('')
    } catch (error) {
      setAuthMessage(normalizeOtpError(error, 'send'))
    } finally {
      setLoading(false)
    }
  }

  async function handleVerifyOtp(event) {
    if (event?.preventDefault) {
      event.preventDefault()
    }

    setAuthMessage('')

    if (!supabase?.auth) {
      setAuthMessage('Supabase auth is not configured.')
      return
    }

    const trimmedEmail = email.trim()
    const trimmedOtp = otp.trim()

    if (!trimmedEmail) {
      setAuthMessage('Enter a valid email.')
      return
    }

    if (!trimmedOtp || trimmedOtp.length !== 6) {
      setAuthMessage('Enter the 6-digit code.')
      return
    }

    setLoading(true)

    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email: trimmedEmail,
        token: trimmedOtp,
        type: 'email',
      })

      if (error) {
        throw error
      }

      const nextUser = data?.user ?? data?.session?.user ?? null
      if (nextUser) {
        setUser(nextUser)
      }

      resetAuthFlow({ clearEmail: true })
      setIsProfileOpen(false)
      navigate('/account')
    } catch (error) {
      setAuthMessage(normalizeOtpError(error, 'verify'))
    } finally {
      setLoading(false)
    }
  }

  async function handleLogout() {
    setAuthMessage('')

    if (!supabase?.auth) {
      setUser(null)
      setAuthMode('default')
      resetAuthFlow()
      setIsProfileOpen(false)
      navigate('/')
      return
    }

    setLoading(true)

    try {
      const { error } = await supabase.auth.signOut()

      if (error) {
        throw error
      }

      setUser(null)
      setAuthMode('default')
      resetAuthFlow()
      setIsProfileOpen(false)
      setShowAdminHQ(false)
      navigate('/')
    } catch (error) {
      setAuthMessage(
        error instanceof Error ? error.message : 'Unable to sign out right now.',
      )
    } finally {
      setLoading(false)
    }
  }

  function openDropBagModal() {
    setShowDropBagModal(true)
    setIsProfileOpen(false)
  }

  function closeDropBagModal() {
    setShowDropBagModal(false)
    setBagAmount(100)
    setCustomBagAmount('')
  }

  function openMilieuModal() {
    const currentView = getMilieuViewFromCurrentLocation()

    setMilieuActiveView(currentView ?? 'menu')
    setIsMilieuOpen(true)
    setIsProfileOpen(false)
  }

  function closeMobileMenu() {
    setIsMobileMenuOpen(false)
    setIsMobileVibesExpanded(false)
  }

  function toggleMegaSearch() {
    setIsProfileOpen(false)
    setIsMegaSearchOpen((currentValue) => !currentValue)
  }

  function closeMegaSearch() {
    setIsMegaSearchOpen(false)
  }

  function buildSearchPath(searchTerm) {
    const trimmedSearchTerm = String(searchTerm ?? '').trim()

    if (!trimmedSearchTerm) {
      return '/search'
    }

    const searchParams = new URLSearchParams()
    searchParams.set('q', trimmedSearchTerm)

    return `/search?${searchParams.toString()}`
  }

  function submitSearchTerm(searchTerm) {
    const trimmedSearchTerm = String(searchTerm ?? '').trim()

    setActiveVibe('all')
    setSearchQuery(trimmedSearchTerm)
    setIsMegaSearchOpen(false)
    setIsMobileMenuOpen(false)
    setIsProfileOpen(false)

    navigate(buildSearchPath(trimmedSearchTerm))
  }

  function handleSearchSuggestionClick(searchTerm) {
    submitSearchTerm(searchTerm)
  }

  async function handleDropBag(event) {
    if (event?.preventDefault) {
      event.preventDefault()
    }

    const checkoutAmount = customBagAmount.trim() ? customBagAmount : bagAmount

    void user
    closeDropBagModal()

    try {
      await startRazorpayCheckout({
        amount: checkoutAmount,
        description: 'Dropping a bag',
        name: 'Aiko Vibe',
        notes: {
          bag_amount: String(checkoutAmount),
          checkout_type: 'drop_bag',
        },
      })
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Unable to start the Razorpay checkout right now.'

      console.error('Razorpay checkout failed.', error)
      window.alert(errorMessage)
    }
  }

  const hideNavRoutes = ['/checkout', '/milieu']
  const isHiddenRoute = hideNavRoutes.some((path) =>
    location.pathname.toLowerCase().includes(path),
  )

  if (isHiddenRoute) return null

  return (
    <div className="fixed top-0 left-0 w-full z-[50] mt-0">
      <AnnouncementBar />
      <header className="relative flex flex-row items-center justify-between w-full px-6 py-4 bg-white border-b border-gray-100">
        <div className="flex flex-row items-center justify-start gap-6 flex-1">
          <button
            type="button"
            onClick={() => {
              setIsProfileOpen(false)
              setIsMegaSearchOpen(false)

              if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
                window.dispatchEvent(new CustomEvent('aikovibe:desktop-menu-open'))
                return
              }

              setIsMobileMenuOpen((currentValue) => !currentValue)
            }}
            aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-vibe-menu"
            className="p-1 text-black hover:opacity-70 transition-opacity cursor-pointer border-none bg-transparent flex items-center justify-center"
          >
            <span className="hidden lg:flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="18" y2="18"/></svg>
            </span>
            <span className="flex items-center justify-center lg:hidden">
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" strokeWidth={1.85} />
              ) : (
                <Menu className="h-6 w-6" strokeWidth={1.85} />
              )}
            </span>
          </button>

          <nav className="hidden lg:flex flex-row items-center gap-6 text-sm font-medium tracking-widest uppercase mt-[2px]">
            <Link
              to="/"
              onClick={() => {
                setActiveVibe('all')
                closeTransientPanels()
              }}
              aria-current={isHomePage ? 'page' : undefined}
              className="text-[11px] font-bold uppercase tracking-[0.22em] text-black transition-colors hover:text-black/60"
            >
              HOME
            </Link>
            <Link
              to="/offers"
              onClick={closeTransientPanels}
              aria-current={isOffersPage ? 'page' : undefined}
              className="text-[11px] font-bold uppercase tracking-[0.22em] text-black transition-colors hover:text-black/60"
            >
              OFFERS
            </Link>
            <Link
              to="/sale"
              onClick={closeTransientPanels}
              aria-current={isSalePage ? 'page' : undefined}
              className="text-[11px] font-bold uppercase tracking-[0.22em] text-red-600 transition-colors hover:text-red-700"
            >
              SALE
            </Link>
            <Link
              to="/about"
              onClick={closeTransientPanels}
              aria-current={isAboutPage ? 'page' : undefined}
              className="text-[11px] font-bold uppercase tracking-[0.22em] text-black transition-colors hover:text-black/60"
            >
              ABOUT US
            </Link>
          </nav>
        </div>

        <div className="flex flex-row items-center justify-center flex-1">
          <Link
            to="/"
            onClick={() => {
              setActiveVibe('all')
              closeTransientPanels()
            }}
            className="text-3xl font-black tracking-tighter text-black"
          >
            AIKOVIBE
          </Link>
        </div>

        <div className="flex flex-row items-center justify-end gap-4 flex-1">
          <IconButton
            label={isMegaSearchOpen ? 'Close search products' : 'Search products'}
            onMouseDown={(event) => {
              event.preventDefault()
            }}
            onClick={toggleMegaSearch}
            aria-expanded={isMegaSearchOpen}
            aria-controls="mega-search-overlay"
          >
            <Search className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.75} />
          </IconButton>

          <div className="relative z-50">
            <IconButton
              label="User account"
              onClick={() => {
                setIsMegaSearchOpen(false)
                setIsProfileOpen((currentValue) => !currentValue)
              }}
              className="relative z-50"
            >
              <User className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.75} />
            </IconButton>

            {isProfileOpen ? (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setIsProfileOpen(false)}
                />

                <div className="absolute right-0 z-50 mt-4 w-72 rounded-2xl border border-gray-800 bg-[#111] p-5 text-white shadow-2xl">
                  <AuthPanel
                    authMode={authMode}
                    authMessage={authMessage}
                    email={email}
                    isOtpSent={isOtpSent}
                    loading={loading}
                    onOpenAdminHQ={() => setShowAdminHQ(true)}
                    onOpenDropBag={openDropBagModal}
                    onOpenLoginMode={openLoginMode}
                    onOpenMilieu={openMilieuModal}
                    onOpenSignupMode={openSignupMode}
                    onSendOtp={handleSendOtp}
                    onSignOut={handleLogout}
                    onVerifyOtp={handleVerifyOtp}
                    otp={otp}
                    setEmail={setEmail}
                    setAuthMessage={setAuthMessage}
                    setOtp={setOtp}
                    user={user}
                  />
                </div>
              </>
            ) : null}
          </div>

          <IconButton
            label="Wishlist"
            onClick={() => {
              setIsMegaSearchOpen(false)
              setIsProfileOpen(false)
              navigate('/wishlist')
            }}
          >
            <Heart className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.75} />
          </IconButton>

          <CartButton count={cartCount} onClick={() => setIsCartOpen(true)} />
        </div>
      </header>

      {isMegaSearchOpen ? (
        <div
          id="mega-search-overlay"
          className="absolute top-full left-0 z-50 w-full bg-white shadow-md p-6"
          role="dialog"
          aria-label="Mega search overlay"
        >
          <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
            <form
              className="flex items-start gap-4 sm:items-center"
              onSubmit={(event) => {
                event.preventDefault()
                submitSearchTerm(searchQuery)
              }}
            >
              <input
                type="text"
                name="mega-search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    closeMegaSearch()
                  }
                }}
                placeholder="Search for your Vibe..."
                autoComplete="off"
                autoFocus
                className="min-w-0 flex-1 border-b border-transparent bg-transparent px-0 pb-3 text-xl font-medium tracking-tight text-black outline-none placeholder:text-black/25 focus:border-black sm:text-2xl"
              />

              <button
                type="button"
                onClick={closeMegaSearch}
                aria-label="Close search overlay"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-black transition-colors hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/15"
              >
                <X className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.75} />
              </button>
            </form>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-black/40">
                Top Searches
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                {megaSearchSuggestions.map((searchTerm) => (
                  <button
                    key={searchTerm}
                    type="button"
                    onClick={() => handleSearchSuggestionClick(searchTerm)}
                    className="rounded-full border border-black/10 px-3 py-1.5 text-xs font-medium text-black transition-colors hover:border-black hover:bg-black hover:text-white"
                  >
                    {searchTerm}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="w-full max-w-[100vw] overflow-x-hidden border-b border-black/10 bg-white lg:hidden">
        <div className="no-scrollbar flex w-full max-w-full items-center gap-3 overflow-x-auto whitespace-nowrap px-4 py-3 [-webkit-overflow-scrolling:touch] sm:px-6">
          {navItems.map((item) => (
            <VibeButton
              key={item.value}
              activeVibe={activeVibe}
              item={item}
              setActiveVibe={setActiveVibe}
              value={item.value}
              to={getCollectionPathForVibe(item.value)}
              className="px-3 py-1.5 text-[11px]"
            />
          ))}
          {categoryNavItems.map((item) => (
            <HeaderPageLink
              key={item.to}
              to={item.to}
              isActive={
                item.to === '/collections/caps-hats'
                  ? isCapsHatsPage
                  : isComboDealsPage
              }
              onClick={closeTransientPanels}
              className="px-3 py-1.5 text-[11px]"
            >
              {item.label}
            </HeaderPageLink>
          ))}
          <HeaderPageLink
            to="/custom-vibe"
            isActive={isCustomVibePage}
            onClick={closeTransientPanels}
            className="px-3 py-1.5 text-[11px]"
          >
            Custom Vibe
          </HeaderPageLink>
          <HeaderPageLink
            to="/offers"
            isActive={isOffersPage}
            onClick={closeTransientPanels}
            className="px-3 py-1.5 text-[11px]"
          >
            OFFERS
          </HeaderPageLink>
          <HeaderPageLink
            to="/sale"
            isActive={isSalePage}
            onClick={closeTransientPanels}
            className="px-3 py-1.5 text-[11px]"
          >
            SALE
          </HeaderPageLink>
          <HeaderPageLink
            to="/about"
            isActive={isAboutPage}
            onClick={closeTransientPanels}
            className="px-3 py-1.5 text-[11px]"
          >
            ABOUT US
          </HeaderPageLink>
        </div>
      </div>

      {isMobileMenuOpen ? (
        <>
          <button
            type="button"
            aria-label="Close vibe menu overlay"
            onClick={closeMobileMenu}
            className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px] lg:hidden"
          />

          <div
            id="mobile-vibe-menu"
            className="absolute left-0 right-0 top-full z-50 border-b border-black/10 bg-white px-5 py-4 shadow-[0_20px_40px_rgba(0,0,0,0.08)] sm:px-8 lg:hidden"
          >
            <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.26em] text-black/40">
                Select a vibe
              </p>
              <button
                type="button"
                onClick={closeMobileMenu}
                aria-label="Close vibe menu"
                className="grid h-10 w-10 place-items-center rounded-full text-black transition-colors hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/15"
              >
                <X className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.75} />
              </button>
            </div>

            <div className="mx-auto mt-4 flex w-full max-w-7xl flex-col gap-2">
              {menuItems.map((item) =>
                item.type === 'dropdown' ? (
                  <div key={item.label} className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setIsMobileVibesExpanded((currentValue) => !currentValue)
                      }
                      aria-expanded={isMobileVibesExpanded}
                      aria-controls="mobile-all-vibes-list"
                      className="flex w-full items-center justify-between rounded-full border border-black/10 bg-white px-4 py-3 text-xs font-bold uppercase tracking-[0.18em] text-black transition-all duration-300 hover:border-black/20 hover:bg-black/[0.03]"
                    >
                      <span>{item.label}</span>
                      <ChevronRight
                        className={`h-4 w-4 transition-transform duration-200 ${
                          isMobileVibesExpanded ? 'rotate-90' : ''
                        }`}
                        strokeWidth={1.8}
                      />
                    </button>

                    {isMobileVibesExpanded ? (
                      <div
                        id="mobile-all-vibes-list"
                        className="grid grid-cols-1 gap-2 pl-2 sm:grid-cols-2"
                      >
                        {item.links.map((link) => (
                          <VibeButton
                            key={link.value}
                            activeVibe={activeVibe}
                            item={link}
                            setActiveVibe={setActiveVibe}
                            value={link.value}
                            to={link.path}
                            onClick={closeMobileMenu}
                          />
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <HeaderPageLink
                    key={item.path}
                    to={item.path}
                    isActive={item.isActive}
                    onClick={closeMobileMenu}
                    className="px-3 py-1.5 text-[11px]"
                  >
                    {item.label}
                  </HeaderPageLink>
                ),
              )}
            </div>
          </div>
        </>
      ) : null}
      {isMilieuOpen ? (
        <DeferredBoundary>
          <MilieuModal
            isOpen={isMilieuOpen}
            onClose={() => setIsMilieuOpen(false)}
            user={user}
            activeView={milieuActiveView}
            setActiveView={setMilieuActiveView}
            reviews={reviews}
            setReviews={setReviews}
          />
        </DeferredBoundary>
      ) : null}

      {showAdminHQ && user?.email === CTO_EMAIL ? (
        <AdminHQ onClose={() => setShowAdminHQ(false)} />
      ) : null}

      {showDropBagModal ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm"
          onClick={closeDropBagModal}
        >
          <div
            className="relative w-full max-w-sm rounded-2xl bg-[#F0F0F3] p-0 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="drop-bag-title"
          >
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <p className="text-xs text-gray-400 font-medium tracking-widest uppercase mb-1">Customer Portal</p>
                  <h3 className="text-4xl font-bold tracking-tight text-black mb-2">dropping a bag</h3>
                  <p className="text-md text-gray-600">full of chips 🍟</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDropBagModal(false)}
                  aria-label="Close drop bag modal"
                  className="text-gray-400 hover:text-black transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
              </div>

              <div className="flex gap-4 mb-8">
                <button
                  onClick={() => { setBagAmount(100); setCustomBagAmount(''); }}
                  className={`flex-1 p-4 rounded-2xl border text-left transition-all ${bagAmount === 100 && !customBagAmount ? 'bg-black text-white border-black shadow-lg scale-105' : 'bg-[#F9F9FB] border-gray-100 text-black hover:border-gray-300'}`}
                >
                  <p className="text-xs font-bold tracking-widest uppercase mb-1 opacity-70">Bag</p>
                  <p className="text-2xl font-bold">₹100</p>
                </button>

                <button
                  onClick={() => { setBagAmount(250); setCustomBagAmount(''); }}
                  className={`flex-1 p-4 rounded-2xl border text-left transition-all ${bagAmount === 250 && !customBagAmount ? 'bg-black text-white border-black shadow-lg scale-105' : 'bg-[#F9F9FB] border-gray-100 text-black hover:border-gray-300'}`}
                >
                  <p className="text-xs font-bold tracking-widest uppercase mb-1 opacity-70">Bag</p>
                  <p className="text-2xl font-bold">₹250</p>
                </button>

                <button
                  onClick={() => { setBagAmount(500); setCustomBagAmount(''); }}
                  className={`flex-1 p-4 rounded-2xl border text-left transition-all ${bagAmount === 500 && !customBagAmount ? 'bg-black text-white border-black shadow-lg scale-105' : 'bg-[#F9F9FB] border-gray-100 text-black hover:border-gray-300'}`}
                >
                  <p className="text-xs font-bold tracking-widest uppercase mb-1 opacity-70">Bag</p>
                  <p className="text-2xl font-bold">₹500</p>
                </button>
              </div>

              <div className="mb-8">
                <label className="block text-sm font-medium text-black mb-4">Custom chips... (e.g., 1000)</label>
                <input
                  id="drop-bag-amount"
                  type="number"
                  name="custom-bag-amount"
                  placeholder="Custom chips... (e.g., 1000)"
                  value={customBagAmount}
                  onChange={(e) => { setCustomBagAmount(e.target.value); setBagAmount(null); }}
                  className="w-full bg-transparent border-b border-gray-200 focus:border-black outline-none py-3 text-lg transition-colors placeholder:text-gray-300"
                />
              </div>

              <button
                onClick={handleDropBag}
                className="w-full bg-black text-white py-4 rounded-full font-bold text-lg hover:scale-[1.02] transition-transform shadow-xl"
              >
                Drop the bag ✨
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default Navbar
